import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Observable, lastValueFrom, map } from 'rxjs';

import { ITimeMarker } from '@vbrick/vbrick-player/app/player/videogular/controls/VbTimeMarkers.Component';

import { ApprovalStepFindingsSavedDTO, ContentReviewComplianceAlertReplyDTO, ContentReviewComplianceAlertReplyDeletedDTO, ContentReviewDTO, ContentReviewEntryUserDetails, ContentReviewManualCommentAddedDTO, ContentReviewManualCommentDeletedDTO, ContentReviewsManualCommentRepliedDTO, ContentReviewsManualCommentReplyDeletedDTO, IVideoReviewData, VideoReviewFindingType, VideoReviewTimeMarkerCSSClass } from './VideoReviewTypes';
import { IUnsubscribe } from 'rev-shared/push/IUnsubscribe';
import { PushBus } from 'rev-shared/push/PushBus.Service';
import { PushService } from 'rev-shared/push/PushService';
import { VIDEO_PLAYBACK_ROUTE } from 'rev-shared/media/Constants';
import { escape } from 'rev-shared/util';
import { formatTimeToSeconds, formatTimespan } from 'rev-shared/date/DateFormatters';
import { getUserInitials } from 'rev-shared/util/Util.Service';
import { orderBy } from 'rev-shared/util/SortUtil';

import styles from './VideoReview.module.less';

@Injectable({
	providedIn: 'root'
})
export class VideoReviewService {
	private readonly styles = styles;
	private isComplianceTabProcessing: boolean = false;
	private isManualCommentsTabProcessing: boolean = false;
	private reviewDataSubject$: BehaviorSubject<IVideoReviewData> = new BehaviorSubject<IVideoReviewData>({ manualComments: [], complianceAlerts: [], stepMapping: [] });

	public reviewData$: Observable<IVideoReviewData> = this.reviewDataSubject$.asObservable();
	public reviewMarkers$: Observable<ITimeMarker[]> = this.reviewDataSubject$.pipe(
		map(reviewData => [
			...reviewData.complianceAlerts.flatMap(alert =>
				alert.occurrences.map(occurrence => {
					return {
						cssClassName: VideoReviewTimeMarkerCSSClass.Alert,
						startTime: occurrence.segment,
						endTime: null,
						id: alert.id,
						isClickDisabled: false
					};
				})
			),
			...reviewData.manualComments
				.filter(comment => comment.segment !== null)
				.map(comment => {
					return {
						cssClassName: comment.user.profileImageUri ? VideoReviewTimeMarkerCSSClass.CommentWithPicture : VideoReviewTimeMarkerCSSClass.CommentWithInitials,
						startTime: comment.segment,
						endTime: null,
						id: comment.id,
						contentHtml: comment.user.profileImageUri ? this.getPictureTimeMarkerHtml(comment.user.profileImageUri) : this.getInitialsTimeMarkerHtml(comment.user),
						contentHtmlCssClass: this.styles.circleMarkerWrapper
					};
				})
		])
	);
	public noComplianceAlerts$: Observable<boolean> = this.reviewDataSubject$.pipe(
		map(reviewData => reviewData.stepMapping.some(step => step.jobId) && !reviewData.complianceAlerts.length)
	);
	public contentAnalysisNotRan$: Observable<boolean> = this.reviewDataSubject$.pipe(
		map(reviewData => reviewData.stepMapping.every(step => !step.jobId) && !reviewData.complianceAlerts.length)
	);

	constructor(
		private http: HttpClient,
		private PushBus: PushBus,
		private PushService: PushService
	) { }

	public get reviewData(): IVideoReviewData {
		return this.reviewDataSubject$.value;
	}

	public clearServiceData(): void {
		this.reviewDataSubject$.next({ manualComments: [], complianceAlerts: [], stepMapping: [] });
	}

	public highlightContainerById(markerId: string) {
		const reviewData = this.reviewDataSubject$.value;
		reviewData.complianceAlerts.forEach(alert => {
			alert.isContainerHighlighted = alert.id === markerId;
		});
		reviewData.manualComments.forEach(comment => {
			comment.isContainerHighlighted = comment.id === markerId;
		});
		this.reviewDataSubject$.next(reviewData);
	}

	public getVideoReviewData(videoId: string): Promise<IVideoReviewData> {
		return lastValueFrom(this.http.get<any>(`/media/videos/${videoId}/content-reviews`))
			.then((contentReviews: ContentReviewDTO[]) => {
				const reviewData = {
					stepMapping: contentReviews.map(contentReview => {
						return {
							stepId: contentReview.stepId,
							stepName: contentReview.stepName,
							jobId: contentReview.jobId
						};
					}),
					complianceAlerts: orderBy(
						contentReviews.flatMap(contentReview =>
							contentReview.complianceAlerts.map(alert => {
								return {
									id: alert.id,
									finding: alert.finding,
									findingType: alert.findingType as VideoReviewFindingType,
									count: alert.count,
									occurrences: alert.occurrences.map(occurence => {
										return {
											segment: formatTimeToSeconds(occurence.value.start),
											segmentText: occurence.value.text
										};
									}),
									replies: alert.replies.map(reply => {
										return {
											reply: reply.comment,
											replyId: reply.id,
											findingId: alert.id,
											user: reply.user,
											when: reply.when,
										};
									}),
									stepName: contentReview.stepName,
									stepId: contentReview.stepId,
									isContainerHighlighted: false
								};
							})
						),
						alert => alert.occurrences[0]?.segment, true
					),
					manualComments: orderBy(
						contentReviews.flatMap(contentReview =>
							contentReview.comments.map(manualComment => {
								return {
									id: manualComment.id,
									segment: manualComment.segment ? formatTimeToSeconds(manualComment.segment) : null,
									comment: manualComment.comment,
									user: manualComment.user,
									when: manualComment.when,
									replies: manualComment.replies.map(reply => {
										return {
											reply: reply.comment,
											replyId: reply.id,
											commentId: reply.commentId,
											user: reply.user,
											when: reply.when,
										};
									}),
									stepName: contentReview.stepName,
									stepId: contentReview.stepId,
									isContainerHighlighted: false
								};
							})
						),
						comment => comment.when, true
					)
				};
				this.reviewDataSubject$.next(reviewData);
				return reviewData;
			});
	}

	public subscribePush(videoId: string): IUnsubscribe {
		return this.PushBus.subscribe(videoId, VIDEO_PLAYBACK_ROUTE, {

			ApprovalStepFindingsSaved: (event: ApprovalStepFindingsSavedDTO) => {
				const reviewData = this.reviewDataSubject$.value;

				const mappedStep = reviewData.stepMapping.find(step => step.stepId === event.stepId);
				if (mappedStep) {
					mappedStep.jobId = event.jobId;
				}

				event.complianceAlerts.map(alert => {
					reviewData.complianceAlerts.push({
						id: alert.id,
						finding: alert.finding,
						findingType: alert.findingType as VideoReviewFindingType,
						count: alert.count,
						occurrences: alert.occurrences.map(occurence => {
							return {
								segment: formatTimeToSeconds(occurence.value.start),
								segmentText: occurence.value.text
							};
						}),
						replies: [],
						stepName: event.stepName,
						stepId: event.stepId,
						isContainerHighlighted: false
					});
				});

				this.reviewDataSubject$.next(reviewData);
			},

			ComplianceAlertsReplyAdded: (event: ContentReviewComplianceAlertReplyDTO) => {
				this.setComplianceTabProcessingOverlay(false);
				const reviewData = this.reviewDataSubject$.value;
				const alert = reviewData.complianceAlerts.find(alert => alert.id === event.findingId);
				if (alert) {
					alert.replies.push({
						reply: event.comment,
						replyId: event.replyId,
						findingId: event.findingId,
						user: event.user,
						when: event.when
					});
				}
				this.reviewDataSubject$.next(reviewData);
			},

			ComplianceAlertsReplyDeleted: (event: ContentReviewComplianceAlertReplyDeletedDTO) => {
				this.setComplianceTabProcessingOverlay(false);
				const reviewData = this.reviewDataSubject$.value;
				const alert = reviewData.complianceAlerts.find(alert => alert.id === event.findingId);
				if (alert) {
					alert.replies = alert.replies.filter(reply => reply.replyId !== event.replyId);
				}
				this.reviewDataSubject$.next(reviewData);
			},

			ContentReviewsCommentAdded: (event: ContentReviewManualCommentAddedDTO) => {
				this.setManualCommentsTabProcessingOverlay(false);
				const reviewData = this.reviewDataSubject$.value;
				reviewData.manualComments.push({
					id: event.id,
					segment: event.segment ? formatTimeToSeconds(event.segment) : null,
					comment: event.comment,
					user: event.user,
					when: event.when,
					replies: [],
					stepName: event.stepName,
					stepId: event.stepId,
					isContainerHighlighted: false
				});
				this.reviewDataSubject$.next(reviewData);
			},

			ContentReviewsCommentDeleted: (event: ContentReviewManualCommentDeletedDTO) => {
				this.setManualCommentsTabProcessingOverlay(false);
				const reviewData = this.reviewDataSubject$.value;
				reviewData.manualComments = reviewData.manualComments.filter(comment => comment.id !== event.commentId);
				this.reviewDataSubject$.next(reviewData);
			},

			ContentReviewsCommentReplied: (event: ContentReviewsManualCommentRepliedDTO) => {
				this.setManualCommentsTabProcessingOverlay(false);
				const reviewData = this.reviewDataSubject$.value;
				const comment = reviewData.manualComments.find(comment => comment.id === event.commentId);
				if (comment) {
					comment.replies.push({
						reply: event.comment,
						replyId: event.id,
						commentId: event.commentId,
						user: event.user,
						when: event.when
					});
				}
				this.reviewDataSubject$.next(reviewData);
			},

			ContentReviewsReplyDeleted: (event: ContentReviewsManualCommentReplyDeletedDTO) => {
				this.setManualCommentsTabProcessingOverlay(false);
				const reviewData = this.reviewDataSubject$.value;
				const comment = reviewData.manualComments.find(comment => comment.id === event.commentId);
				if (comment) {
					comment.replies = comment.replies.filter(reply => reply.replyId !== event.replyId);
				}
				this.reviewDataSubject$.next(reviewData);
			}

		});
	}

	public addAlertReply(videoId: string, stepId: string, findingId: string, comment: string): Promise<void> {
		this.setComplianceTabProcessingOverlay(true);
		return this.PushService.dispatchCommand('media:AddComplianceAlertsReply', {
			videoId,
			stepId,
			findingId,
			comment
		});
	}

	public deleteAlertReply(videoId: string, stepId: string, findingId: string, replyId: string): Promise<void> {
		this.setComplianceTabProcessingOverlay(true);
		return this.PushService.dispatchCommand('media:DeleteComplianceAlertsReply', {
			videoId,
			stepId,
			findingId,
			replyId
		});
	}

	public addManualComment(videoId: string, stepId: string, segment: number, comment: string): Promise<void> {
		this.setManualCommentsTabProcessingOverlay(true);
		return this.PushService.dispatchCommand('media:AddContentReviewsComment', {
			videoId,
			stepId,
			segment: segment !== null ? formatTimespan(segment) : null,
			comment: comment
		});
	}

	public deleteManualComment(videoId: string, stepId: string, commentId: string): Promise<void> {
		this.setManualCommentsTabProcessingOverlay(true);
		return this.PushService.dispatchCommand('media:DeleteContentReviewsComment', {
			videoId,
			stepId,
			commentId
		});
	}

	public addManualCommentReply(videoId: string, stepId: string, commentId: string, reply: string): Promise<void> {
		this.setManualCommentsTabProcessingOverlay(true);
		return this.PushService.dispatchCommand('media:ReplyContentReviewsComment', {
			videoId,
			stepId,
			commentId,
			reply
		});
	}

	public deleteManualCommentReply(videoId: string, stepId: string, commentId: string, replyId: string): Promise<void> {
		this.setManualCommentsTabProcessingOverlay(true);
		return this.PushService.dispatchCommand('media:DeleteContentReviewsReply', {
			videoId,
			stepId,
			commentId,
			replyId
		});
	}

	public setComplianceTabProcessingOverlay(isProcessing: boolean): void {
		this.isComplianceTabProcessing = isProcessing;
	}

	public setManualCommentsTabProcessingOverlay(isProcessing: boolean): void {
		this.isManualCommentsTabProcessing = isProcessing;
	}

	public get showComplianceTabProcessingOverlay(): boolean {
		return this.isComplianceTabProcessing;
	}

	public get showManualCommentsTabProcessingOverlay(): boolean {
		return this.isManualCommentsTabProcessing;
	}

	public getPictureTimeMarkerHtml(imageUri: string): string {
		return `<img src="${escape(imageUri)}" />`;
	}

	public getInitialsTimeMarkerHtml(user: ContentReviewEntryUserDetails): string {
		return `<span>${escape(getUserInitials(user.firstName, user.lastName) || (user.userName || '').charAt(0))}</span>`;
	}

}
